import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { CONVERSION_TYPE_LABELS, type ConversionType } from '../../ReportingV2/components/Conversions';

export type { ConversionType };
export { CONVERSION_TYPE_LABELS };

/**
 * Dimensions whose fact table carries no `conversion_type` column —
 * GEOGRAPHY-only, same exclusion Unified Reporting's own Geo/Publishers tabs
 * apply (`NOT_TYPE_SPLITTABLE` in Conversions.tsx): combining either with
 * `conversion_type` 400s ("no common source table").
 */
const CONVERSION_TYPE_INCOMPATIBLE_DIMS = new Set(['geo_zip_label', 'geo_city']);

export const isConversionTypeSplittable = (dims: string[]): boolean =>
  !dims.some(d => CONVERSION_TYPE_INCOMPATIBLE_DIMS.has(d));

const ALL_CONVERSION_TYPES: ConversionType[] = ['web', 'listen_through', 'foot_traffic'];

/**
 * Which type toggles to offer — Web is always offered so the control is never
 * empty before the fetch resolves; Listen-through/Foot traffic only appear
 * once a row actually carries that type (same convention as Unified
 * Reporting's own `availableTypes`, Conversions.tsx).
 */
export const availableConversionTypes = (rows: Array<{ conversion_type?: unknown }>): ConversionType[] => {
  const present = new Set(rows.map(r => r.conversion_type));
  return ALL_CONVERSION_TYPES.filter(t => t === 'web' || present.has(t));
};

/** Falls back to the first available type when the widget's saved choice
 * (or no choice at all) isn't one of the types this data actually has. */
export const resolveConversionType = (
  requested: ConversionType | undefined,
  available: ConversionType[],
): ConversionType => (requested && available.includes(requested) ? requested : (available[0] ?? 'web'));

/** flank#5098 AC1 — every conversions column/card/chart names its type. */
export const conversionsLabel = (type: ConversionType | undefined): string =>
  type ? `Conversions (${CONVERSION_TYPE_LABELS[type]})` : 'Conversions';

/**
 * Sum of `conversions` across rows of one `conversion_type`, ungrouped — for
 * a KPI/Scorecard/Goal/Funnel's single headline number.
 */
export const sumConversionsForType = (rows: ReportRow[], type: ConversionType): number =>
  rows.filter(r => r.conversion_type === type).reduce((sum, r) => sum + Number(r.conversions ?? 0), 0);

/**
 * Sum of `conversions` across rows of one `conversion_type`, per value of
 * `groupKey` (e.g. `dsp`) — for a Table/Pivot/Donut column whose other rows
 * (not just the selected type) share the group. A group with no row at all
 * for this type is simply absent from the map (real zero, not "—": AdsWizz
 * reporting 0 web conversions is as true as it reporting 962 listen-throughs).
 */
export const buildConversionsByGroup = (
  rows: ReportRow[],
  type: ConversionType,
  groupKey: string,
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const row of rows) {
    if (row.conversion_type !== type) continue;
    const key = String(row[groupKey] ?? '');
    map.set(key, (map.get(key) ?? 0) + Number(row.conversions ?? 0));
  }
  return map;
};
